import { enqueueSnackbar } from 'notistack';
import useSWRImmutable from 'swr/immutable';
import settings from '/@/core/settings';
import { _ } from '/@/locale';
import { axFetch } from '/@/store/session/actions';

export const getCatalogItemsByBrandFromApi = async ({
    country,
    brand,
    crop,
    target,
    cropProtectionCrop,
    cropProtectionProductType,
    sortField,
    zone,
    technology,
    densityCoefficientsEnvironment,
    zonesPlantingDate,
    published,
    zoneIds,
    page = 1,
    pageSize = 10000,
}: {
    country: string;
    brand: string;
    crop: string;
    target?: string;
    cropProtectionCrop?: string;
    cropProtectionProductType?: string;
    sortField?: string;
    zone?: number;
    technology?: number;
    page?: number;
    pageSize?: number;
    densityCoefficientsEnvironment?: number;
    zonesPlantingDate?: string;
    published?: boolean;
    zoneIds?: number[];
}) => {
    if (crop === 'crop_protection') {
        try {
            const params = new URLSearchParams();
            params.append('country', country);
            params.append('brand', brand);
            params.append('page', page.toString());
            params.append('page_size', pageSize.toString());
            if (cropProtectionCrop) {
                params.append('crop', cropProtectionCrop.toLowerCase());
            }
            if (cropProtectionProductType) {
                params.append('product', cropProtectionProductType);
            }
            if (target) {
                params.append('target', target);
            }
            if (sortField) {
                params.append('sort_by', sortField);
            }
            if (published !== undefined) {
                params.append('published', published.toString());
            }
            const apiUrl = new URL(
                `${settings.urls.apiDomain}/core/v1/crop-protection/catalog/?${params.toString()}`,
                window.location.origin
            );

            const res = await axFetch.get(apiUrl.toString());
            return res.data;
        } catch (error) {
            console.error(error);
            return [];
        }
    } else {
        try {
            const params = new URLSearchParams();
            params.append('country', country);
            params.append('crop', crop.toLowerCase());
            params.append('brand', brand);
            params.append('page', page.toString());
            params.append('page_size', pageSize.toString());
            if (zone) {
                params.append('zone', zone.toString());
            }
            if (technology) {
                params.append('technology', technology.toString());
            }
            if (densityCoefficientsEnvironment) {
                params.append('density_coefficients__environment', densityCoefficientsEnvironment.toString());
            }
            if (zonesPlantingDate) {
                params.append('zones__planting_date', zonesPlantingDate);
            }
            if (zoneIds) {
                params.append('zones', zoneIds.join(','));
            }
            const apiUrl = new URL(
                `${settings.urls.apiDomain}/core/v1/product-catalog/?${params.toString()}`,
                window.location.origin
            );

            const res = await axFetch.get(apiUrl.toString());
            return res.data;
        } catch (error) {
            console.error(error);
            return [];
        }
    }
};

export const getCatalogItemFromApi = async ({ id, crop }, cache) => {
    const cacheKey = JSON.stringify({ key: 'catalogProduct', id, crop });
    const cachedData = cache.get(cacheKey);
    if (cachedData) {
        return cachedData;
    }

    try {
        let apiUrl;
        if (crop === 'crop_protection') {
            apiUrl = new URL(
                `${settings.urls.apiDomain}/core/v1/crop-protection/catalog/${id}/`,
                window.location.origin
            );
        } else {
            const cropForApi = crop === 'refuge' ? 'corn' : crop;
            apiUrl = new URL(
                `${settings.urls.apiDomain}/core/v2/product-catalog/${id}/?crop=${cropForApi}`,
                window.location.origin
            );
        }

        const res = await axFetch.get(apiUrl.toString());
        const result = res.data;

        cache.set(cacheKey, result);

        return result;
    } catch (error) {
        console.error(error);
        return {};
    }
};

export const getCatalogItemsFromApi = async ({ ids, crop }) => {
    try {
        const allFeaturesPromises = [];
        for (const id of ids) {
            allFeaturesPromises.push(getCatalogItemFromApi({ id, crop }));
        }

        const allFeatures = await Promise.all(allFeaturesPromises);

        return allFeatures;
    } catch (error) {
        console.error(error);
        return [];
    }
};

export const generateCatalogPDF = async ({
    country,
    type,
    slug,
    zoneId,
    itemId,
    comparisonsIds,
    language,
    fileName = '',
}) => {
    try {
        const params = new URLSearchParams({
            appDomain: location.origin,
            country,
            type,
            slug,
            zoneId,
            itemId,
            comparisonsIds,
            language,
        });
        enqueueSnackbar(_('Generando PDF...'), {
            variant: 'info',
        });

        const apiUrl = new URL(
            `${settings.urls.apiDomain}/core/v1/product-catalog/pdf/?${params.toString()}`,
            window.location.origin
        );
        const res = await axFetch.get(apiUrl.toString(), { responseType: 'blob' });

        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(res.data);
        link.download = fileName || `${_('Catálogo')} PDF.pdf`;
        link.click();
        window.URL.revokeObjectURL(link.href);
        enqueueSnackbar(_('PDF descargado exitosamente!'), {
            variant: 'success',
        });
    } catch (error) {
        enqueueSnackbar(_('Hubo un error al intentar descargar el PDF'), {
            variant: 'error',
        });
    }
};

export const getCatalogZonesFromApi = async ({
    page,
    pageSize = 10,
    crop,
    country,
    type,
}: {
    page: number;
    pageSize?: number;
    crop?: string;
    country?: string;
    type?: string;
}) => {
    const apiUrl = new URL(`${settings.urls.apiDomain}/core/v1/catalog/zones/`, window.location.origin);

    const params = new URLSearchParams();
    params.append('page', String(page));
    params.append('page_size', String(pageSize));
    if (crop) params.append('crop', String(crop));
    if (country) params.append('country', String(country));
    if (type) params.append('type', String(type));
    apiUrl.search = params.toString();

    const res = await axFetch.get(apiUrl.toString());
    return res.data;
};

export const useCatalogZones = ({
    page,
    pageSize = 10,
    crop,
    country,
    type,
}: {
    page: number;
    pageSize?: number;
    crop?: string;
    country?: string;
    type?: string;
}) => {
    const key = { key: 'useCatalogZones', page, pageSize, crop, country, type };
    return useSWRImmutable(key, getCatalogZonesFromApi);
};

export const getCatalogConfigFromApi = async ({ brand }: { brand: number }) => {
    const apiUrl = new URL(`${settings.urls.apiDomain}/core/v1/catalog/config/${brand}`, window.location.origin);
    const res = await axFetch.get(apiUrl.toString());
    return res.data;
};

export const useCatalogConfig = ({ brand }: { brand: number }) => {
    const key = { key: 'useCatalogConfig', brand };
    return useSWRImmutable(key, getCatalogConfigFromApi);
};
